interface ContentPowerCard {
  caption: string
  /** Inline composition of label + value (rendered side-by-side on mobile, stacked on desktop) */
  label: string
  value: string
  meta: string
  iconUrl: string
  /** Optional 3rd line for card03 (방송 및 언론) only */
  extraLine?: string
}

interface ContentPowerSectionProps {
  titleLine1: string
  titleLine2Plain: string
  titleLine2Highlight: string
  cards: ContentPowerCard[]
}

function Card({ card }: { card: ContentPowerCard }) {
  const hasExtra = Boolean(card.extraLine)
  return (
    <article
      className={`relative w-full max-w-[272px] bg-white rounded-[24px] lg:rounded-[32px] shadow-[0px_8px_8px_rgba(0,0,0,0.2)] flex flex-col items-center text-center gap-2 lg:gap-4 pt-12 px-6 lg:pt-22 ${
        hasExtra ? 'pb-6 lg:pb-9' : 'pb-6 lg:pb-16'
      }`}
    >
      {/* Icon — sits ABOVE the card on lg, INSIDE the card on mobile */}
      <img
        src={card.iconUrl}
        alt=""
        aria-hidden
        className="absolute -top-10 lg:-top-14 w-20 h-20 lg:w-30 lg:h-30 object-contain"
      />

      <p className="font-bold lg:font-semibold text-qb-primary text-[18px] lg:text-[20px] leading-[1.5] tracking-[-0.6px] lg:tracking-normal">
        {card.caption}
      </p>

      <div
        className={`flex ${hasExtra ? 'flex-col' : 'flex-row lg:flex-col'} items-center gap-2 text-qb-text-high tracking-[-0.6px] leading-[1.4]`}
      >
        <p className={`font-medium text-[18px] ${hasExtra ? 'lg:text-[20px] lg:leading-[1.5]' : 'lg:text-[24px]'}`}>
          {card.label}
        </p>
        <p className={`font-bold ${hasExtra ? 'text-[22px] lg:text-[28px]' : 'text-[24px] lg:text-[32px]'}`}>
          {card.value}
        </p>
      </div>

      {card.extraLine && (
        <p className="font-medium text-qb-text-high text-[18px] lg:text-[20px] leading-[1.5] tracking-[-0.6px]">
          {card.extraLine}
        </p>
      )}

      <p className="font-medium text-qb-text-low text-[16px] lg:text-[18px] leading-[1.5] tracking-[-0.6px] lg:tracking-normal">
        {card.meta}
      </p>
    </article>
  )
}

export function ContentPowerSection({
  titleLine1,
  titleLine2Plain,
  titleLine2Highlight,
  cards,
}: ContentPowerSectionProps) {
  return (
    <section id="content-power" data-marketing-section="" className="relative w-full overflow-hidden tracking-[-0.6px]">
      {/* Top half — blue */}
      <div className="absolute inset-0 bg-qb-primary" aria-hidden />
      {/* Bottom band — dark (covers ~145px mobile / ~240px desktop from bottom) */}
      <div className="absolute inset-x-0 bottom-0 h-[145px] lg:h-[240px] bg-qb-text-high" aria-hidden />

      <div className="relative mx-auto max-w-[1136px] px-6 lg:px-0 py-14 lg:py-24 flex flex-col gap-14 lg:gap-16 items-center">
        {/* Title */}
        <div className="flex flex-col gap-2.5 items-center text-center text-white font-bold text-[28px] lg:text-[40px] leading-[1.4] lg:leading-[1.2]">
          <p className="whitespace-pre-line lg:whitespace-normal">{titleLine1}</p>
          <p>
            <span>{titleLine2Plain}</span>
            <span className="text-[#fe0]">{titleLine2Highlight}</span>
          </p>
        </div>

        {/* Cards — 1 col mobile, 2 col tablet, 4 col desktop. Padding-top so floating icons don't clip. */}
        <div data-testid="stat-card-grid" className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-x-5 md:gap-y-16 lg:gap-4 justify-items-center pt-10 lg:pt-12 md:max-w-[580px] md:mx-auto lg:max-w-none">
          {cards.map((card) => (
            <Card key={card.caption} card={card} />
          ))}
        </div>
      </div>
    </section>
  )
}
